import { useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('submitting');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qth2me3oq4jqumtqj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  return (
    <>
      {/* JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 黄网识别与防范指南',
            description: '了解网络安全教育团队的使命、愿景和联系方式',
            url: 'https://fangfanhuangwang.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '黄网识别与防范指南',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '网络安全教育团队',
            url: 'https://fangfanhuangwang.com',
            description: '致力于普及网络安全知识，帮助公众识别和防范黄色网站，保护个人隐私与家庭和谐。',
            contactPoint: {
              '@type': 'ContactPoint',
              contactType: 'customer support',
              email: 'contact@wangluoanquan.org',
              availableLanguage: ['Chinese'],
            },
          }),
        }}
      />

      <main className="pt-24 pb-16 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="mb-8">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="mb-16">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
              关于我们
            </h1>
            <p className="text-lg text-foreground-600 leading-relaxed max-w-3xl">
              网络安全教育团队是一群致力于网络安全公益事业的专业人士，
              我们相信知识是防范网络风险的最佳武器。
            </p>
          </section>

          {/* Mission & Vision */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="p-6 md:p-8 rounded-2xl bg-primary-50 border border-primary-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 mb-4">
                <i className="ri-focus-3-line text-xl"></i>
              </div>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-3">我们的使命</h2>
              <p className="text-foreground-600 leading-relaxed">
                通过专业的网络安全知识普及，帮助社会公众特别是青少年群体
                识别和防范网络色情内容，保护个人隐私与身心健康，为构建清朗
                网络空间贡献力量。我们坚信，教育是最好的防火墙。
              </p>
            </div>
            <div className="p-6 md:p-8 rounded-2xl bg-accent-50 border border-accent-100">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600 mb-4">
                <i className="ri-lightbulb-line text-xl"></i>
              </div>
              <h2 className="font-heading text-xl font-bold text-foreground-900 mb-3">我们的愿景</h2>
              <p className="text-foreground-600 leading-relaxed">
                我们希望创造一个没有网络色情危害的数字世界，让每一个网民
                都能安全、健康地使用互联网。我们期待通过持续的教育和倡导，
                让网络安全意识深入人心，让防范技能成为每个人的基本素养。
              </p>
            </div>
          </section>

          {/* Core Values */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8 text-center">
              核心价值观
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              {[
                {
                  icon: 'ri-shield-star-line',
                  title: '专业可信',
                  desc: '依托专业的网络安全知识和法律法规，提供准确可靠的信息与建议。',
                },
                {
                  icon: 'ri-heart-pulse-line',
                  title: '关爱守护',
                  desc: '以关爱之心关注每一位网民的网络安全，特别关注青少年群体的健康成长。',
                },
                {
                  icon: 'ri-group-line',
                  title: '公益为先',
                  desc: '坚持公益性质，免费向公众提供网络安全教育资源，普惠社会大众。',
                },
              ].map((value) => (
                <div
                  key={value.title}
                  className="p-6 rounded-xl bg-background-50 border border-background-200 text-center"
                >
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600 mb-4">
                    <i className={`${value.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {value.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation */}
          <section className="mb-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8 text-center">
              社会声誉
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                {
                  title: '权威内容审核',
                  desc: '所有文章内容均经过网络安全专家和法律顾问的双重审核，确保信息的准确性和合法性。我们不传播未经证实的内容，坚持严谨负责的内容发布原则。',
                },
                {
                  title: '广泛社会认可',
                  desc: '我们的教育资源已被多所学校和社区组织采用，累计服务超过10万用户。多次获得教育部门和网络监管部门的肯定与推荐。',
                },
                {
                  title: '持续内容更新',
                  desc: '团队持续跟踪网络安全领域的最新动态和法规变化，定期更新网站内容，确保提供的信息始终与最新的网络环境和法律要求保持一致。',
                },
                {
                  title: '用户隐私保护',
                  desc: '我们严格遵守隐私保护相关法律法规，不收集用户个人信息，不投放追踪性广告，始终将用户隐私和安全放在首位。',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 rounded-xl bg-background-50 border border-background-200"
                >
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    <i className="ri-check-double-line text-primary-500 mr-2"></i>
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="mb-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-8 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Contact Info */}
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">电子邮件</h3>
                    <p className="text-sm text-foreground-500">contact@wangluoanquan.org</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">举报热线</h3>
                    <p className="text-sm text-foreground-500">12321 网络不良与垃圾信息举报受理中心</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 shrink-0 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                    <i className="ri-global-line text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground-900 mb-1">举报网站</h3>
                    <a
                      href="https://www.12377.cn"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      www.12377.cn
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-50 rounded-2xl border border-background-200 p-6 md:p-8">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                  发送留言
                </h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-accent-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-accent-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>
                  <div className="honeypot-field">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '发送留言'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm text-center">
                      <i className="ri-check-line mr-1"></i>
                      {formMessage}
                    </div>
                  )}
                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-accent-50 text-accent-700 text-sm text-center">
                      <i className="ri-error-warning-line mr-1"></i>
                      {formMessage}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}